'use client';

import React, { useState } from 'react';
import { Plus, BarChart2, List } from 'lucide-react';
import ExpenseSummaryCards from './ExpenseSummaryCards';
import ExpenseTable from './ExpenseTable';
import ExpenseDashboard from './ExpenseDashboard';
import ExpenseForm from './ExpenseForm';
import { FARMS_LIST, EXPENSE_CATEGORIES } from './expensesData';
import { useRole } from '@/lib/useRole';

const TABS = [
  { id: 'records', label: 'Expense Records', icon: List },
  { id: 'dashboard', label: 'Summary Dashboard', icon: BarChart2 },
];

export default function ExpensesContent() {
  const [activeTab, setActiveTab] = useState('records');
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [farmFilter, setFarmFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const { can, roleLabel, roleColors } = useRole();

  const canCreateExpense = can('create_expenses');
  const canApproveExpense = can('approve_expenses');

  function handleSave(data: Record<string, unknown>) {
    setToast(`Expense logged successfully for ${data.farm ?? 'farm'}.`);
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500 mt-1">
            Log farm costs by category (feed, medicine, utilities, labor), scoped to farm and branch, with a summary dashboard.
          </p>
          {!canApproveExpense && (
            <span className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${roleColors.bg} ${roleColors.text}`}>
              {roleLabel} — View{canCreateExpense ? ' & Submit' : ''} Only
            </span>
          )}
        </div>
        {canCreateExpense && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm flex-shrink-0"
          >
            <Plus size={16} />
            Log Expense
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <ExpenseSummaryCards />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'records' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search description, vendor, ref…"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 w-56"
            />
            <select value={farmFilter} onChange={e => setFarmFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">All Farms</option>
              {FARMS_LIST.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">All Categories</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <ExpenseTable farmFilter={farmFilter} categoryFilter={categoryFilter} search={search} canApprove={canApproveExpense} />
        </>
      )}

      {activeTab === 'dashboard' && (
        <>
          {/* Farm filter for dashboard */}
          <div className="flex gap-3 mb-5">
            <select value={farmFilter} onChange={e => setFarmFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">All Farms</option>
              {FARMS_LIST.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
            </select>
          </div>
          <ExpenseDashboard farmFilter={farmFilter} />
        </>
      )}

      {showForm && <ExpenseForm onClose={() => setShowForm(false)} onSave={handleSave} />}
    </div>
  );
}
